import { zPoCreateExclusivityYearData } from '@repo/sdk/zod'
import { Button } from '@repo/ui/button'
import { InlineFilterMultiSelect } from '@repo/ui/commons/inline-filters/inline-filter-options'
import { InlineFilters } from '@repo/ui/commons/inline-filters/inline-filters'
import { InlineFiltersReset } from '@repo/ui/commons/inline-filters/inline-filters-reset'
import { ListInputSearch } from '@repo/ui/commons/list-input-search'
import { PageHeader } from '@repo/ui/commons/page-header'
import { Tabs, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'
import { pageTitle, type TKey, translateApp, useTranslation } from '@/lib/i18n'
import { poConfigQueryOptions } from '@/routes/(private)/documents/-lib/query-options'
import { AddPublisherDialog } from '@/routes/(private)/esclusive/-components/add-publisher-dialog'
import {
	PublisherSheet,
	type SheetTarget
} from '@/routes/(private)/esclusive/-components/publisher-sheet'
import { PublishersTable } from '@/routes/(private)/esclusive/-components/publishers-table'
import { YearControls } from '@/routes/(private)/esclusive/-components/year-controls'
import {
	REGION_LABEL_KEYS,
	REGIONS
} from '@/routes/(private)/esclusive/-lib/regions'

const CHANNELS = ['all', 'esclusiva', 'ingrosso'] as const

// `catch` rather than `default`: a broken filter in the URL (truncated link,
// hand-edited region) falls back to the initial value instead of the error
// boundary.
const searchSchema = z.object({
	search: z.string().trim().catch(''),
	canale: z.enum(CHANNELS).catch('all'),
	regioni: z.array(z.enum(REGIONS)).catch([]),
	anno: zPoCreateExclusivityYearData.shape.body.shape.data.shape.year.catch(
		() =>
			Number(
				new Intl.DateTimeFormat('en', {
					year: 'numeric',
					timeZone: 'Europe/Rome'
				}).format(new Date())
			)
	),
	ordine: z.literal('nome').catch('nome'),
	verso: z.enum(['asc', 'desc']).catch('asc')
})

export const Route = createFileRoute('/(private)/esclusive/')({
	staticData: { breadcrumb: 'esclusive:page_title' },
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(poConfigQueryOptions()),
	component: Esclusive,
	validateSearch: searchSchema,
	search: {
		middlewares: [
			stripSearchParams(searchSchema.omit({ anno: true }).parse({}))
		]
	},
	head: () => ({
		meta: [{ title: pageTitle(translateApp('it', 'esclusive:page_title')) }]
	})
})

function Esclusive() {
	const params = Route.useSearch()
	// Changing year discards the open editor and its unsaved draft.
	return <AnnualPublishers key={params.anno} />
}

function AnnualPublishers() {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const config = useSuspenseQuery(poConfigQueryOptions()).data.config
	const annual = config.esclusiveByYear.find(
		(entry) => entry.year === params.anno
	)
	const [target, setTarget] = useState<SheetTarget | null>(null)
	const [addOpen, setAddOpen] = useState(false)
	// Rows just saved stay listed even when they no longer match the filters
	// they were edited under (remove Abruzzo while filtering by Abruzzo), until
	// those filters change: otherwise they would vanish the moment you save.
	const filtersKey = JSON.stringify([
		params.search,
		params.canale,
		params.regioni
	])
	const [lastSaved, setLastSaved] = useState({
		names: new Set<string>(),
		filtersKey
	})
	// A filter change drops the pins for good: coming back to the same filters
	// must not bring them back.
	if (lastSaved.filtersKey !== filtersKey && lastSaved.names.size > 0) {
		setLastSaved({ names: new Set(), filtersKey })
	}
	const pinned =
		lastSaved.filtersKey === filtersKey
			? lastSaved.names
			: new Set<string>()

	const rows = (annual?.publishers ?? []).map((entry) => ({
		...entry,
		id: entry.name
	}))
	const exclusiveCount = rows.filter((row) => row.regions.length > 0).length

	const needle = params.search.toLowerCase()
	const direction = params.verso === 'asc' ? 1 : -1
	const visible = rows
		.filter(
			(row) =>
				pinned.has(row.name) ||
				((params.canale === 'all' ||
					(params.canale === 'esclusiva') ===
						row.regions.length > 0) &&
					(params.regioni.length === 0 ||
						params.regioni.some((region) =>
							row.regions.includes(region)
						)) &&
					row.name.toLowerCase().includes(needle))
		)
		.toSorted((a, b) => direction * a.name.localeCompare(b.name, 'it'))

	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<div className='animate-enter'>
				<PageHeader
					description={t('esclusive:page_description')}
					endSlot={
						<Button
							className='shrink-0'
							disabled={!annual}
							onClick={() => setAddOpen(true)}
							type='button'
							variant='outline'
						>
							<Plus />
							{t('esclusive:add.button')}
						</Button>
					}
					title={t('esclusive:page_title')}
				/>
			</div>

			<YearControls />
			{!annual && (
				<p className='px-6 pb-4 text-muted-foreground text-sm'>
					{t('esclusive:year.missing', { year: params.anno })}
				</p>
			)}

			<Filters
				counts={{
					all: rows.length,
					esclusiva: exclusiveCount,
					ingrosso: rows.length - exclusiveCount
				}}
			/>

			<div className='flex min-h-0 flex-1 animate-enter flex-col px-6 pb-4 [--enter-step:3]'>
				<PublishersTable
					emptyTitle={
						rows.length === 0
							? t(
									annual
										? 'esclusive:year.empty'
										: 'esclusive:year.not_created'
								)
							: t('esclusive:no_match')
					}
					onOpen={(name) => setTarget({ name, isNew: false })}
					pinned={pinned}
					rows={visible}
				/>
			</div>

			<PublisherSheet
				onClose={() => setTarget(null)}
				onSaved={(result) => {
					setLastSaved({ names: new Set(result.names), filtersKey })
					setTarget(null)
				}}
				target={target}
			/>
			<AddPublisherDialog
				onOpenChange={setAddOpen}
				onPick={(pick) => {
					setAddOpen(false)
					setTarget(pick)
				}}
				open={addOpen}
			/>
		</div>
	)
}

function Filters(props: { counts: Record<(typeof CHANNELS)[number], number> }) {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const navigate = Route.useNavigate()
	const setFilters = (next: Partial<typeof params>) =>
		navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })

	return (
		// Same filter bar as the document toolbar.
		<div className='animate-enter px-6 pb-2 [--enter-step:1]'>
			<div className='flex flex-wrap items-center gap-1.5 rounded-lg bg-muted/70 p-1.5 2xl:gap-2 [&_[data-slot=input-group]]:bg-background [&_[data-slot=select-trigger]]:bg-background [&_[data-slot=tabs-list]]:bg-background'>
				<Tabs
					onValueChange={(value) => {
						const channel = CHANNELS.find(
							(option) => option === value
						)
						if (channel) {
							setFilters({ canale: channel })
						}
					}}
					value={params.canale}
				>
					<TabsList>
						{CHANNELS.map((channel) => (
							<TabsTrigger key={channel} value={channel}>
								{t(CHANNEL_LABEL_KEYS[channel])}
								<span className='font-normal text-muted-foreground tabular-nums'>
									{props.counts[channel]}
								</span>
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
				<InlineFilters className='flex-1'>
					<InlineFilterMultiSelect
						label={t('esclusive:filter_region')}
						onValueChange={(regioni) => setFilters({ regioni })}
						options={REGIONS.map((region) => ({
							label: t(REGION_LABEL_KEYS[region]),
							value: region
						}))}
						value={params.regioni}
					/>
					{(params.search !== '' ||
						params.canale !== 'all' ||
						params.regioni.length > 0) && (
						<InlineFiltersReset
							onReset={() =>
								setFilters({
									search: '',
									canale: 'all',
									regioni: []
								})
							}
						/>
					)}
					{/* Search sits at the end of the row: the filters read left to
				    right and the box lands where the eye looks for it. */}
					<div className='ml-auto'>
						<ListInputSearch
							placeholder={t('esclusive:search_placeholder')}
						/>
					</div>
				</InlineFilters>
			</div>
		</div>
	)
}

const CHANNEL_LABEL_KEYS = {
	all: 'esclusive:channel.all',
	esclusiva: 'esclusive:channel.esclusiva',
	ingrosso: 'esclusive:channel.ingrosso'
} as const satisfies Record<(typeof CHANNELS)[number], TKey>
